import type { ItineraryDetailSearch } from '@itineraries/widgets/itinerary-view-tabs';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  filterDaysByChapter,
  getChapterById,
  readChapters,
} from '../model/chapter';
import { useChapterEditorStore } from '../model/use-chapter-editor-store';
import { ChapterSelectorView } from './chapter-selector-view';

type ChapterSelectorProps = {
  sections: unknown;
  // Day list the day-filter cascade applies against — used purely for
  // the "Showing N of M days" label inside the pill. The actual day
  // filtering lives at the `ItineraryDays` boundary so the
  // collapse/drag state isn't reset by a chapter re-pick.
  days: { date: string | null }[];
};

// Component layer for the filter pill. Returns null when no chapters
// exist — chapter creation/editing lives in the dedicated "Chapters"
// button in the view-tabs row, which drives `useChapterEditorStore`.
// This widget is only responsible for filtering an already-populated
// chapter set.
export const ChapterSelector = ({ sections, days }: ChapterSelectorProps) => {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ItineraryDetailSearch;
  const activeChapterId = search.chapter ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const openEditor = useChapterEditorStore((s) => s.open);

  const chapters = readChapters(sections);
  const activeChapter = getChapterById(chapters, activeChapterId);
  const filteredDays = filterDaysByChapter(days as never, activeChapter);

  // If the active chapter id falls off the list (e.g. a save removed
  // it) clear the URL param so the day list stops being filtered to a
  // ghost id. `sections` is `undefined` until the version query resolves —
  // an id missing from a not-yet-loaded set isn't stale, and clearing there
  // would eat a `?chapter=` deep link. Deps are primitives on purpose:
  // `readChapters` allocates a fresh array per render, so keying on it
  // re-fires this effect on the very render its own `navigate` causes, and
  // that navigate is queued behind the pending route load — the URL never
  // changes to satisfy the guard, so it spins until the query lands.
  const isVersionLoaded = sections !== undefined;
  const hasActiveChapter = activeChapter !== null;
  useEffect(() => {
    if (!activeChapterId || !isVersionLoaded || hasActiveChapter) return;
    navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        chapter: undefined,
      }),
      replace: true,
    });
  }, [activeChapterId, hasActiveChapter, isVersionLoaded, navigate]);

  if (chapters.length === 0) return null;

  const handlePickChapter = (id: string | null) => {
    setIsOpen(false);
    navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        chapter: id ?? undefined,
      }),
      replace: true,
    });
  };

  return (
    <ChapterSelectorView
      activeChapter={activeChapter}
      chapters={chapters}
      filteredDayCount={filteredDays.length}
      isOpen={isOpen}
      onManage={() => {
        setIsOpen(false);
        openEditor();
      }}
      onPickChapter={handlePickChapter}
      onRequestClose={() => setIsOpen(false)}
      onToggle={() => setIsOpen((v) => !v)}
      totalDayCount={days.length}
    />
  );
};
